import React from 'react';
import {
  Box,
  Container,
  Heading,
  Text,
  SimpleGrid,
  VStack,
  HStack,
  Flex,
  Image,
} from '@chakra-ui/react';
import { FaPencilRuler, FaCode, FaMobileAlt } from 'react-icons/fa';

const About = () => {
  const stats = [
    { number: '5+', label: 'Years in Tech' },
    { number: '15+', label: 'Projects Completed' },
    { number: '10+', label: 'Technologies' },
  ];

  const superpowers = [
    {
      icon: FaPencilRuler,
      title: 'Design',
      description: 'I enjoy applying my knowledge of UI/UX design to create aesthetically pleasing and user-friendly templates.'
    },
    {
      icon: FaCode,
      title: 'Web Development',
      description: 'Frontend or backend, I actively engage with new technologies to craft responsive websites and refine my skills.'
    },
    {
      icon: FaMobileAlt,
      title: 'Mobile Development',
      description: 'Working with Android and iOS, I am employing my skills to solve your problems right in the palm of your hands.'
    }
  ];

  return (
    <Box
      as="section"
      id="about"
      bg="#69c7bc"
      py={20}
      position="relative"
    >
      {/* Top Wave */}
      <Box
        position="absolute"
        top={0}
        left={0}
        width="100%"
        overflow="hidden"
        lineHeight={0}
        transform="rotate(180deg)"
      >
        <svg
          viewBox="0 0 1440 100"
          preserveAspectRatio="none"
          style={{ width: '100%', height: '100px' }}
        >
          <path
            d="M0,50 C360,20 720,80 1440,50 L1440,0 L0,0 Z"
            fill="#69c7bc"
          />
        </svg>
      </Box>

      <Container maxW="1200px" pt={16}>
        {/* Section Title */}
        <Heading
          as="h2"
          fontSize={{ base: '2.9rem', md: 'clamp(3.6rem, 5vw, 4.6rem)' }}
          fontWeight="800"
          color="white"
          lineHeight="1.1"
          textAlign="center"
          mb={12}
        >
          <Box
            as="span"
            position="relative"
            display="inline-block"
            zIndex={1}
            color="white"
            _before={{
              content: '""',
              position: 'absolute',
              bottom: '5px',
              left: '-5px',
              right: '-5px',
              height: '18px',
              bg: 'var(--chakra-colors-gray-800)',
              zIndex: -1,
              transform: 'skewY(-1deg)',
              borderRadius: '4px',
            }}
          >
            Why work with me?
          </Box>
        </Heading>

        {/* Profile Info Section */}
        <VStack spacing={8} maxW="800px" mx="auto" my={12}>
          <Text fontSize="1.1rem" color="#2d3436" textAlign="center" lineHeight="1.8">
            With 10+ years of experience in the banking sector and a Master's in Economics, I understand how technology drives business results. As a full-stack developer and QA engineer, I create reliable, user-friendly applications that solve real business problems.
          </Text>

          {/* Stats */}
          <SimpleGrid
            columns={{ base: 1, md: 3 }}
            spacing={8}
            w="100%"
            mt={10}
          >
            {stats.map((stat, index) => (
              <Box
                key={index}
                bg="white"
                p={{ base: 6, md: 8 }}
                borderRadius="15px"
                boxShadow="0 4px 15px rgba(0, 0, 0, 0.1)"
                textAlign="center"
              >
                <Text
                  fontSize={{ base: '3xl', md: '2.5rem' }}
                  fontWeight="800"
                  color="#2d3436"
                  mb={2}
                >
                  {stat.number}
                </Text>
                <Text fontSize="1rem" color="#2d3436">
                  {stat.label}
                </Text>
              </Box>
            ))}
          </SimpleGrid>
        </VStack>

        {/* Superpowers Layout */}
        <Flex
          direction={{ base: 'column', lg: 'row' }}
          gap={{ base: 8, lg: 16 }}
          align="center"
          mt={16}
        >
          {/* Superpowers List */}
          <VStack flex={1} spacing={{ base: 8, lg: 12 }} align="stretch">
            {superpowers.map((power, index) => (
              <HStack key={index} spacing={{ base: 4, lg: 8 }} align="flex-start">
                <Box
                  w={{ base: '70px', lg: '80px' }}
                  h={{ base: '70px', lg: '80px' }}
                  minW={{ base: '70px', lg: '80px' }}
                  bg="white"
                  borderRadius="full"
                  display="flex"
                  alignItems="center"
                  justifyContent="center"
                  fontSize={{ base: '1.5rem', lg: '2rem' }}
                  color="#2d3436"
                  border="3px solid #2d3436"
                  boxShadow="0 4px 15px rgba(0, 0, 0, 0.1)"
                  transition="all 0.3s"
                >
                  <Box as={power.icon} />
                </Box>
                <VStack align="flex-start" spacing={3} flex={1}>
                  <Heading as="h3" fontSize={{ base: 'xl', lg: '1.8rem' }} fontWeight="700" color="#2d3436">
                    {power.title}
                  </Heading>
                  <Text fontSize={{ base: 'sm', lg: '1.05rem' }} color="#2d3436" lineHeight="1.7">
                    {power.description}
                  </Text>
                </VStack>
              </HStack>
            ))}
          </VStack>

          {/* Superpowers Image */}
          <Box flex={1} position="relative" display={{ base: 'none', lg: 'block' }}>
            <Box position="relative" display="flex" justifyContent="center" alignItems="center">
              <Image
                src="/img/about.jpg"
                alt="Ramil Sharapov"
                w="100%"
                maxW="550px"
                h="450px"
                borderRadius="20px"
                objectFit="cover"
                objectPosition="center top"
                position="relative"
                zIndex={2}
                fallbackSrc="https://via.placeholder.com/550x450"
              />
              {/* Decorative Dots */}
              <Box
                position="absolute"
                top="-30px"
                right="-30px"
                w="150px"
                h="150px"
                backgroundImage="radial-gradient(circle, white 2px, transparent 2px)"
                backgroundSize="15px 15px"
                zIndex={1}
              />
            </Box>
          </Box>
        </Flex>
      </Container>
    </Box>
  );
};

export default About;
